/**
 * Group Transform Service
 *
 * Lets the user move/rotate/scale several selected objects together with one
 * gizmo, pivoting around the selection centroid.
 *
 * The sandbox's transform gizmo is the Babylon Inspector's, which attaches to a
 * single node (the `selectedEntity`). To transform a group we:
 *
 *   1. Create a temporary pivot TransformNode at the centroid of the selection
 *      and point the Inspector gizmo at it (selectedEntity = pivot).
 *   2. On gizmo DRAG START (attached node === our pivot), reparent each selected
 *      object's transform under the pivot, preserving world transform.
 *   3. On gizmo DRAG END, bake: un-parent each object (preserving world
 *      transform), record a per-object TransformCommand, and push them as ONE
 *      CompositeCommand.
 *
 * Reparenting happens only during an active drag. Between drags the objects sit
 * under their normal parents, so undo/redo (which restores LOCAL transforms)
 * works correctly — applying a snapshot while parented under the pivot would
 * otherwise corrupt the world pose.
 *
 * The pivot is editor-internal and never serialized. Reparenting uses raw
 * TransformNode.setParent (via the prototype) to bypass the SceneObject parent
 * interceptor / reparent policy — the pivot is not a SceneObject.
 */

import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Vector3, Quaternion } from '@babylonjs/core/Maths/math.vector';
import { PositionGizmo } from '@babylonjs/core/Gizmos/positionGizmo';
import { RotationGizmo } from '@babylonjs/core/Gizmos/rotationGizmo';
import { ScaleGizmo } from '@babylonjs/core/Gizmos/scaleGizmo';
import type { Node } from '@babylonjs/core/node';
import type { Scene } from '@babylonjs/core/scene';
import type { TraceViewerElement, SceneObject } from 'trace-viewer';
import {
    getUndoManager,
    TransformCommand,
    CompositeCommand,
    type TransformSnapshot,
    type UndoCommand,
} from 'trace-viewer/editor';
import type { SelectionModel } from 'trace-viewer/editor';
import { getSelectionService, suppressSelectionBridge, isSelectionBridgeSuppressed } from './selection-bridge-service';
import { getGizmoService } from './gizmo-snap-service';
import { setGroupDragActive } from './group-drag-state';
import { topMostSelected } from './selection-utils';

/** Raw setParent that skips the SceneObject interceptor (pivot isn't a SceneObject). */
function rawSetParent(node: TransformNode, parent: TransformNode | null): void {
    TransformNode.prototype.setParent.call(node, parent);
}

/**
 * Tolerance for treating a transform component as unchanged. Sized to absorb the
 * ULP-level drift from a reparent → un-reparent matrix decompose round-trip,
 * while staying far below any movement a user could intend with a gizmo.
 */
const TRANSFORM_EPSILON = 1e-5;

function approxEqualArr(a: readonly number[], b: readonly number[]): boolean {
    for (let i = 0; i < a.length; i++) {
        if (Math.abs(a[i] - b[i]) > TRANSFORM_EPSILON) return false;
    }
    return true;
}

interface Removable { remove(): void }
/** A drag-capable gizmo instance with an attached node and drag observables. */
interface DragGizmo {
    attachedNode: Node | null;
    onDragStartObservable: { add(cb: () => void): Removable | null };
    onDragEndObservable: { add(cb: () => void): Removable | null };
}

interface GroupMember {
    object: SceneObject;
    originalParent: TransformNode | null;
    before: TransformSnapshot;
}

export class GroupTransformService {
    private readonly model: SelectionModel;
    private readonly scene: Scene;
    private readonly _unsub: () => void;

    private pivot: TransformNode | null = null;
    private members: GroupMember[] = [];
    private dragging = false;
    private readonly _patchCleanup: (() => void)[] = [];
    private readonly _hookedGizmos = new WeakSet<object>();
    private readonly _scaleGizmos = new Set<ScaleGizmo>();
    private readonly _instanceCleanup: Removable[] = [];

    constructor(viewer: TraceViewerElement, model: SelectionModel) {
        this.model = model;
        const scene = viewer.traceScene?.getBabylonScene();
        if (!scene) throw new Error('[GroupTransform] no babylon scene');
        this.scene = scene;

        this._patchGizmoAttach();
        this._unsub = this.model.onChanged(() => this._sync());
    }

    /** Build or tear down the pivot to match the current selection. */
    private _sync(): void {
        if (this.dragging) return;
        const top = topMostSelected(this.model.list());
        this._teardown();
        if (top.length > 1) this._build(top);
    }

    private _centroid(objects: SceneObject[]): Vector3 {
        const c = Vector3.Zero();
        for (const o of objects) {
            o.transform.computeWorldMatrix(true);
            c.addInPlace(o.transform.getAbsolutePosition());
        }
        return c.scaleInPlace(1 / objects.length);
    }

    /** Create the pivot at the centroid and attach the Inspector gizmo to it. */
    private _build(objects: SceneObject[]): void {
        const pivot = new TransformNode('__group_pivot__', this.scene);
        pivot.rotationQuaternion = Quaternion.Identity();
        pivot.position.copyFrom(this._centroid(objects));
        pivot.computeWorldMatrix(true);
        this.pivot = pivot;

        this.members = objects.map(o => ({
            object: o,
            originalParent: (o.transform.parent as TransformNode | null) ?? null,
            before: TransformCommand.capture(o),
        }));

        // Group is active: the per-object undo-gizmo service must stand down while
        // the gizmo manipulates our pivot.
        setGroupDragActive(true);

        // Ensure a transform gizmo mode is active so the group gizmo is visible.
        const gizmo = getGizmoService();
        if (gizmo && !gizmo.gizmoMode) {
            gizmo.gizmoMode = 'translate';
        }

        // Point the Inspector gizmo at the pivot. The selection-bridge's
        // viewport→inspector handler runs LATER in the same event and would reset
        // selectedEntity to the primary object, so re-assert on a microtask after
        // all selection-changed listeners have run. The bridge stays suppressed so
        // this doesn't round-trip into scene.select().
        this._attachGizmoToPivot();
    }

    /** Point the Inspector gizmo at the pivot now and again after the event settles. */
    private _attachGizmoToPivot(): void {
        const pivot = this.pivot;
        if (!pivot) return;
        const set = () => {
            if (this.pivot !== pivot) return; // torn down meanwhile
            const sel = getSelectionService();
            if (!sel) return;
            // Restore the PRIOR suppression state, not an unconditional false —
            // MultiSelectionService may already be holding the bridge suppressed
            // (modifier key down) and this runs in a later microtask.
            const wasSuppressed = isSelectionBridgeSuppressed();
            suppressSelectionBridge(true);
            try {
                sel.selectedEntity = pivot;
            } finally {
                suppressSelectionBridge(wasSuppressed);
            }
        };
        set();
        // Win over the bridge's later viewport→inspector handler in this same event.
        void Promise.resolve().then(set);
    }

    /** Reparent members under the pivot (world-preserving) and snapshot before. */
    private _onDragStart(): void {
        if (!this.pivot || this.members.length === 0) return;
        this.dragging = true;

        // Re-seat the pivot at the current centroid with identity orientation so
        // rotate/scale pivot around the selection center.
        this.pivot.position.copyFrom(this._centroid(this.members.map(m => m.object)));
        this.pivot.rotationQuaternion?.set(0, 0, 0, 1);
        this.pivot.scaling.set(1, 1, 1);
        this.pivot.computeWorldMatrix(true);

        for (const m of this.members) {
            m.before = TransformCommand.capture(m.object);
            rawSetParent(m.object.transform, this.pivot);
        }
    }

    /** Un-parent members (world-preserving), bake, push one CompositeCommand. */
    private _onDragEnd(): void {
        if (!this.pivot || !this.dragging) return;
        this.dragging = false;

        const commands: UndoCommand[] = [];
        for (const m of this.members) {
            rawSetParent(m.object.transform, m.originalParent);
            m.object.transform.computeWorldMatrix(true);
            const after = TransformCommand.capture(m.object);
            if (this._changed(m.before, after)) {
                m.object.captureTransform();
                commands.push(new TransformCommand(m.object, m.before, after));
            }
        }

        if (commands.length > 0 && !getUndoManager().isUndoRedoing) {
            const cmd: UndoCommand = commands.length === 1
                ? commands[0]
                : new CompositeCommand(`Transform ${commands.length} objects`, commands);
            getUndoManager().push(cmd);
        }

        // Rebuild the pivot at the new centroid (selection unchanged) so the gizmo
        // re-attaches with an identity pose for the next drag.
        const top = topMostSelected(this.model.list());
        this._teardown();
        if (top.length > 1) this._build(top);
    }

    private _changed(b: TransformSnapshot, a: TransformSnapshot): boolean {
        // Epsilon (not exact !==): a click with no net movement still round-trips
        // each member through reparent → un-reparent (world-preserving matrix
        // decompose), which can leave a few ULPs of drift in position/rotation/
        // scale. Exact equality would flag that as "changed" and push a no-op
        // undo entry that does nothing visible.
        return (
            !approxEqualArr(b.position, a.position) ||
            !approxEqualArr(b.rotation, a.rotation) ||
            !approxEqualArr(b.scale, a.scale)
        );
    }

    /** Dispose the pivot. Members are not parented under it between drags. */
    private _teardown(): void {
        if (this.pivot) {
            for (const m of this.members) {
                if (m.object.transform.parent === this.pivot) {
                    rawSetParent(m.object.transform, m.originalParent);
                    m.object.transform.computeWorldMatrix(true);
                    m.object.captureTransform();
                }
            }
            this.pivot.dispose();
            this.pivot = null;
        }
        this.members = [];
        this.dragging = false;
        setGroupDragActive(false);
        // Restore per-axis scale handles for single-object use.
        for (const g of this._scaleGizmos) this._setScaleAxesEnabled(g, true);
    }

    /**
     * Patch the gizmo prototypes' `attachedNode` setter so that whenever a node
     * is attached we hook that gizmo instance's drag observables. The drag
     * callbacks act only when the attached node is our pivot, so non-group drags
     * are untouched. Mirrors the proven pattern in undo-gizmo-service.ts.
     */
    private _patchGizmoAttach(): void {
        const self = this;
        const hook = (gizmo: DragGizmo) => {
            if (this._hookedGizmos.has(gizmo)) return;
            this._hookedGizmos.add(gizmo);
            const s = gizmo.onDragStartObservable.add(() => {
                if (gizmo.attachedNode === this.pivot && this.pivot) this._onDragStart();
            });
            const e = gizmo.onDragEndObservable.add(() => {
                if (this.dragging) this._onDragEnd();
            });
            if (s) this._instanceCleanup.push(s);
            if (e) this._instanceCleanup.push(e);
        };

        for (const Ctor of [PositionGizmo, RotationGizmo, ScaleGizmo]) {
            const proto = Ctor.prototype as object;
            for (const prop of ['attachedNode', 'attachedMesh'] as const) {
                const desc = Object.getOwnPropertyDescriptor(proto, prop);
                if (!desc?.set) continue;
                const orig = desc;
                Object.defineProperty(proto, prop, {
                    get: orig.get ? function (this: object) { return orig.get!.call(this) as unknown; } : undefined,
                    set: function (this: DragGizmo, value: unknown) {
                        orig.set!.call(this, value);
                        if (value && 'onDragStartObservable' in this) hook(this);
                        // Group scale must stay UNIFORM: a child rotated vs the pivot
                        // gets a sheared world matrix under non-uniform scale, which
                        // TRS-only storage can't represent (skew during drag, snap on
                        // release). Disable the per-axis scale handles while the scale
                        // gizmo drives our pivot; restore them otherwise.
                        if (this instanceof ScaleGizmo) {
                            self._scaleGizmos.add(this);
                            self._setScaleAxesEnabled(this, value !== self.pivot);
                        }
                    },
                    configurable: true,
                    enumerable: true,
                });
                this._patchCleanup.push(() => Object.defineProperty(proto, prop, orig));
            }
        }
    }

    /** Enable/disable the per-axis scale handles (keeps the uniform-scale box). */
    private _setScaleAxesEnabled(gizmo: ScaleGizmo, enabled: boolean): void {
        gizmo.xGizmo.isEnabled = enabled;
        gizmo.yGizmo.isEnabled = enabled;
        gizmo.zGizmo.isEnabled = enabled;
    }

    dispose(): void {
        for (const r of this._instanceCleanup) r.remove();
        this._instanceCleanup.length = 0;
        for (const undo of this._patchCleanup) undo();
        this._patchCleanup.length = 0;
        this._teardown();
        this._unsub();
    }

    /**
     * @internal Test seam: simulate a gizmo drag of the group by `delta` (world).
     * Exercises the real reparent → move → bake → CompositeCommand path without a
     * live Inspector gizmo. Not used by production code.
     */
    _testDrag(delta: { x?: number; y?: number; z?: number }): void {
        if (!this.pivot) return;
        this._onDragStart();
        this.pivot.position.x += delta.x ?? 0;
        this.pivot.position.y += delta.y ?? 0;
        this.pivot.position.z += delta.z ?? 0;
        this.pivot.computeWorldMatrix(true);
        this._onDragEnd();
    }

    /** @internal Test seam: the current member count (0 when no group pivot). */
    get _testMemberCount(): number {
        return this.members.length;
    }

    /**
     * @internal Test seam: apply the per-axis-scale enable/disable rule for a
     * given attached node, returning the resulting [x,y,z] enabled states. Mirrors
     * what the patched `attachedNode` setter does for a ScaleGizmo: axes are
     * disabled (uniform-only) exactly when the gizmo drives our pivot.
     */
    _testScaleAxesForAttach(gizmo: ScaleGizmo, attached: Node | null): [boolean, boolean, boolean] {
        this._setScaleAxesEnabled(gizmo, attached !== this.pivot);
        return [gizmo.xGizmo.isEnabled, gizmo.yGizmo.isEnabled, gizmo.zGizmo.isEnabled];
    }
}
